import { RemoteServicePriority } from '@store';

export interface RemoteServiceFeatures {
  boiler_id: number;
  point_interval: number;
  point_start: number;
  datalength: number;
  series: RemoteServiceFeature[];
}

export interface RemoteServiceFeature {
  feature: string;
  unit: string;
  name: string;
  data: any[];
}

export interface RemoteServiceFeaturesChartSeries {
  series: RemoteServiceFeature[];
  seriesNameKey: string;
}

export interface RemoteServiceDiagnosisResponse {
  data: {
    boiler_id: string;
    boiler_material_number: string;
    timeframe: RemoteServiceTimeResolution;
    data: DiagnosisData[];
  };
}

export namespace RemoteServiceDiagnosisMetadata {
  export type Audience = AudienceOptions | string;
  export type ReleaseStage = AudienceOptions | string;

  export enum ReleaseStageOptions {
    Beta = 'beta',
    GoldError = 'gold_error',
  }

  export enum AudienceOptions {
    Customer = 'customer',
    Internal = 'internal',
  }
}

export interface RemoteServiceRecommendations {
  boiler_id: string;
  boiler_material_number: string;
  data: RemoteServiceRecommendationsData[];
}

export interface RemoteServiceRecommendationsData {
  recommendation_date: string; // Date
  recommendation_id: string;
  recommendation_name: string;
  recommendation_group: string;
  recommendation_value: RecommendationValue;
  recommendation_score: number;
}

export interface RemoteServiceChartConfig {
  figure: string;
  title: string;
  features: string[];
  scale: {
    [key: string]: boolean;
  };
  type: 'line' | 'pie' | string;
  description: string;
}

export interface RemoteServiceDiagnosisQueryParams {
  boiler_id: string;
  timeframe?: RemoteServiceTimeResolution;
  start_datetime: string;
  end_datetime: string;
}

export interface RemoteServiceSetThresholdQueryParams {
  boiler_id: string;
  group: string;
  name: string;
  value: number;
  comment: string;
}

// Soon to be dropped in favor of data lake endpoints and query
// https://wiki.viessmann-climatesolutions.com/pages/viewpage.action?pageId=416875530
export interface RemoteServiceQueryParams {
  timeframe?: RemoteServiceTimeResolution;
  start_datetime: string;
  end_datetime: string;
  boiler_id: string;
}

export type RemoteServiceTimeResolution = '1d' | '7d';

export interface RecommendationValue {
  group_scores: {
    [key: string]: number;
  };
  config: {
    [key: string]: {
      return_by_missing: number;
      anomaly_weight: number;
      diagnose_period: number;
    };
  };
}

export interface InstallationRecommendation {
  highestRiskValue: number;
  boilerSerial: string;
}

export interface DiagnosisData {
  boiler_id: string;
  boiler_material_number: number;
  diagnosis_date: string;
  diagnosis_id: string;
  diagnosis_name: string;
  diagnosis_group: string;
  diagnosis_value: {
    [key: string]: string | number;
  };
  diagnosis_configurations: {
    group: string;
    name: string;
    unit: string;
    max: number;
    min: number;
    default: number;
    value: number;
    description: string;
    comment: string;
  }[];
  diagnosis_anomaly: string;
  diagnosis_priority: RemoteServicePriority;
  diagnosis_text: string;
  metrics: {
    [key: string]: string | number;
  };
  actions: string[];
  relevant_adf: string[];
  meta_information?: {
    parent_diagnosis?: string;
    release_stage?: RemoteServiceDiagnosisMetadata.ReleaseStage;
    audience?: RemoteServiceDiagnosisMetadata.Audience[];
    version: string;
    [key: string]: any;
  };
}
